'use client';

import { useEffect, useState } from 'react';

import { dequal } from 'dequal';
import Cookies from 'js-cookie';

type UtmDataType = {
    utm_source: string;
    utm_campaign: string;
    utm_medium: string;
    utm_term: string;
    utm_content: string;
    referrer_url: string;
    gclid: string;
    campaign_id: string;
    ad_group_id: string;
    ad_id: string;
};
/**
 * Dual-compatible hook that works in both App Router and Pages Router contexts
 * Extracts UTM tracking parameters from URL and stores them in cookies
 *
 * This implementation uses window.location directly to avoid router-specific hooks,
 * making it compatible with both routing systems.
 */
export function useUtmDataCookie() {
    const [params, setParams] = useState<Partial<UtmDataType>>({});

    // Extract params from URL on mount (client-side only)
    useEffect(() => {
        if (typeof window === 'undefined') return;

        const searchParams = new URLSearchParams(window.location.search);
        setParams({
            utm_source: searchParams.get('utm_source') || undefined,
            utm_campaign: searchParams.get('utm_campaign') || undefined,
            utm_medium: searchParams.get('utm_medium') || undefined,
            utm_term: searchParams.get('utm_term') || undefined,
            utm_content: searchParams.get('utm_content') || undefined,
            gclid: searchParams.get('gclid') || undefined,
            campaign_id: searchParams.get('campaign_id') || undefined,
            ad_group_id: searchParams.get('ad_group_id') || undefined,
            ad_id: searchParams.get('ad_id') || undefined
        });
    }, []);

    useEffect(() => {
        const {
            utm_source,
            utm_campaign,
            utm_medium,
            utm_term,
            utm_content,
            gclid,
            campaign_id,
            ad_group_id,
            ad_id
        } = params;

        const utm_params = [
            { utm_source },
            { utm_campaign },
            { utm_medium },
            { utm_term },
            { utm_content },
            { gclid },
            { campaign_id },
            { ad_group_id },
            { ad_id }
        ].filter((param) => Object.values(param)[0]);

        if (!utm_params?.length) {
            return;
        }

        const UtmData: Partial<UtmDataType> = {};
        for (let i = 0; i < utm_params.length; i++) {
            UtmData[Object.keys(utm_params[i])[0]] = Object.values(utm_params[i])[0] as string;
        }

        if (utm_source) {
            Cookies.set('utm_source', utm_source, {
                domain: '.iconosquare.com',
                expires: 365
            });
        }

        if (!dequal(UtmData, {})) {
            if (!(typeof window === 'undefined')) {
                const referrer = document.referrer;
                if (referrer && !referrer?.includes('.iconosquare.com')) {
                    UtmData.referrer_url = referrer;
                }
            }
            Cookies.set('utm_data', JSON.stringify(UtmData), {
                domain: '.iconosquare.com',
                expires: 365
            });
        }
    }, [params]);
}
